import type { AbaDoPerfil } from "./telas/Perfil"
import type { ReactNode } from "react"

import type { EstadoDoPainel } from "./estado-do-painel"

import type { Perfil as PerfilDaConta } from "./api"
import type { SecaoDoTrilho } from "./casca/Casca"
import * as Icones from "./componentes/Icones"
import type { PosicaoDaBarra } from "./preferencias"
import type { RecorteDaLista } from "./recortes"
import { Avisos } from "./telas/Avisos"
import { Configuracoes } from "./telas/Configuracoes"
import type { AlcanceDeConfiguracoes } from "./api"
import {
  DADOS_DO_ITEM,
  ICONE_DA_AREA,
  type GrupoDeConfiguracao,
  type ItemDeConfiguracao,
} from "./itens-de-configuracao"
import { Contratos } from "./telas/Contratos"
import type { AbaDaFicha } from "./telas/Ficha"
import { Fiscalizacao } from "./telas/Fiscalizacao"
import { Painel } from "./telas/Painel"
import { Perfil } from "./telas/Perfil"
import { Relatorios } from "./telas/Relatorios"

export type RecursosDaTela = {
  abrirContrato: (id: number, aba?: AbaDaFicha) => void
  abrirLista: (recorte: RecorteDaLista | null) => void
  recorte: RecorteDaLista | null
  abrirPerfil: (aba?: AbaDoPerfil) => void
  abaDoPerfil?: AbaDoPerfil
  posicao: PosicaoDaBarra
  aoTrocarPosicao: (nova: PosicaoDaBarra) => void
  aoTrocarSenha: (credencial: string, perfil: PerfilDaConta) => void
  aoMudarDadosDaConta: (perfil: PerfilDaConta) => void
  itemDeConfiguracao: ItemDeConfiguracao | undefined
  alcance: AlcanceDeConfiguracoes | null
  recusaDoAlcance: string | null
  abrirConfiguracao: (item: ItemDeConfiguracao) => void
  entidadeDeConfiguracao: number | undefined
  abrirEntidade: (id: number | null, substituir?: boolean) => void
  aoSaberDaEntidade: (sigla: string | null) => void
  perfilDeContas: string | undefined
  abrirContasDoPerfil: (perfil: string | null, substituir?: boolean) => void
  tipoDoFiltro: string | undefined
  cargoDoFiltro: string | undefined
  sistemaDoFiltro: string | undefined
  abrirConfiguracaoFiltrada: (
    item: ItemDeConfiguracao,
    filtros: { perfil?: string | null; cargo?: string | null; tipo?: string | null; sistema?: string | null },
    substituir?: boolean,
  ) => void
  estadoDoPainel: EstadoDoPainel
  aoMudarPainel: (estado: EstadoDoPainel) => void
}

export type TelaDoTrilho = {
  rotulo: string
  secao: string
  trilha: string
  grupo?: string
  foraDoMenu?: boolean
  icone: Icones.Icone
  desenhar: (recursos: RecursosDaTela) => ReactNode
  emBreve?: boolean
}

export type ChaveDeTela =
  | "painel"
  | "contratos"
  | "avisos"
  | "relatorios"
  | "fiscalizacao"
  | "perfil"
  | "configuracoes"

export const TELAS: Record<ChaveDeTela, TelaDoTrilho> = {
  painel: {
    rotulo: "Painel",
    secao: "Gestão",
    trilha: "Painel",
    icone: Icones.Painel,
    desenhar: (r) => (
      <Painel
        estado={r.estadoDoPainel}
        aoMudarEstado={r.aoMudarPainel}
        aoAbrirContrato={r.abrirContrato}
        aoAbrirLista={r.abrirLista}
      />
    ),
  },
  contratos: {
    rotulo: "Contratos",
    secao: "Gestão",
    trilha: "Lista",
    icone: Icones.Contrato,
    desenhar: (r) => (
      <Contratos
        aoAbrirContrato={r.abrirContrato}
        recorte={r.recorte}
        aoLimparRecorte={() => r.abrirLista(null)}
      />
    ),
  },
  avisos: {
    rotulo: "Avisos",
    secao: "Gestão",
    trilha: "Avisos",
    icone: Icones.Sino,
    desenhar: (r) => <Avisos aoAbrirContrato={r.abrirContrato} />,
  },
  relatorios: {
    rotulo: "Relatórios",
    secao: "Gestão",
    trilha: "Relatórios",
    icone: Icones.Relatorio,
    desenhar: (r) => (
      <Relatorios
        aoAbrirContrato={r.abrirContrato}
        aoAbrirCanaisDeAviso={
          r.alcance?.itens.some((i) => i.chave === "canais" && i.ve) === true ? () => r.abrirConfiguracao("canais") : undefined
        }
      />
    ),
  },
  fiscalizacao: {
    rotulo: "Minha fiscalização",
    secao: "Fiscalização",
    trilha: "Minha fiscalização",
    icone: Icones.Fiscalizacao,
    desenhar: (r) => <Fiscalizacao aoAbrirContrato={r.abrirContrato} />,
  },
  perfil: {
    rotulo: "Meu perfil",
    secao: "Sistema",
    trilha: "Meu perfil",
    grupo: "Sistema",
    foraDoMenu: true,
    icone: Icones.Pessoa,
    desenhar: (r) => (
      <Perfil
        aoAbrirContrato={r.abrirContrato}
        aoTrocarSenha={r.aoTrocarSenha}
        aoMudarDados={r.aoMudarDadosDaConta}
        aba={r.abaDoPerfil ?? "dados"}
        aoTrocarAba={r.abrirPerfil}
        posicao={r.posicao}
        aoTrocarPosicao={r.aoTrocarPosicao}
      />
    ),
  },
  configuracoes: {
    rotulo: "Configurações",
    secao: "Sistema",
    trilha: "Configurações",
    grupo: "Sistema",
    icone: Icones.Configuracao,
    desenhar: (r) => (
      <Configuracoes
        alcance={r.alcance}
        recusa={r.recusaDoAlcance}
        item={r.itemDeConfiguracao}
        aoTrocarItem={r.abrirConfiguracao}
        entidade={r.entidadeDeConfiguracao}
        aoAbrirEntidade={r.abrirEntidade}
        aoSaberDaEntidade={r.aoSaberDaEntidade}
        perfil={r.perfilDeContas}
        aoAbrirContasDoPerfil={r.abrirContasDoPerfil}
        tipo={r.tipoDoFiltro}
        cargo={r.cargoDoFiltro}
        sistema={r.sistemaDoFiltro}
        aoAbrirFiltrada={r.abrirConfiguracaoFiltrada}
        aoAbrirContrato={r.abrirContrato}
      />
    ),
  },
}

export const CHAVES_DE_TELA = Object.keys(TELAS) as ChaveDeTela[]

export function secoesDoTrilho(grupos: GrupoDeConfiguracao[] | null): SecaoDoTrilho<ChaveDeTela>[] {
  const secoes: SecaoDoTrilho<ChaveDeTela>[] = []
  for (const chave of CHAVES_DE_TELA) {
    const tela = TELAS[chave]
    if (tela.foraDoMenu === true) continue
    if (chave === "configuracoes" && grupos !== null && grupos.length === 0) continue
    let secao = secoes.find((s) => s.titulo === tela.secao)
    if (secao === undefined) {
      secao = { titulo: tela.secao, itens: [] }
      secoes.push(secao)
    }
    secao.itens.push({
      chave,
      rotulo: tela.rotulo,
      icone: tela.icone,
      ...(tela.emBreve === true ? { emBreve: true } : {}),
      ...(chave === "configuracoes" && grupos !== null
        ? {
            subitens: grupos.map((grupo) => ({
              chave: grupo.area,
              rotulo: grupo.rotulo,
              endereco: `/configuracoes/${grupo.itens[0] ?? ""}`,
              icone: ICONE_DA_AREA[grupo.area] ?? Icones.Configuracao,
              itens: grupo.itens.map((item) => ({
                chave: item,
                rotulo: DADOS_DO_ITEM[item].rotulo,
                endereco: `/configuracoes/${item}`,
                icone: DADOS_DO_ITEM[item].icone,
              })),
            })),
          }
        : {}),
    })
  }
  return secoes
}
